import React, { useId, useState } from "react";
import { colors, radius, spacing, typography } from "./tokens";

const labelBaseStyle = {
  display: "block",
  marginBottom: spacing.xs,
  color: colors.textMuted,
  fontSize: typography.size.xs,
  fontWeight: typography.weight.medium,
};

const selectBaseStyle = {
  padding: `${spacing.md}px ${spacing.lg}px`,
  borderRadius: radius.sm,
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: colors.border,
  background: colors.surface,
  color: colors.text,
  fontSize: typography.size.base,
  lineHeight: typography.lineHeight.form,
  boxSizing: "border-box",
  transition: "border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease",
};

const selectErrorStyle = {
  borderColor: colors.danger,
};

const selectDisabledStyle = {
  background: colors.disabledBg,
  borderColor: colors.disabledBorder,
  color: colors.disabledText,
  boxShadow: "none",
  cursor: "not-allowed",
  opacity: 1,
};

const errorTextStyle = {
  marginTop: spacing.xs,
  color: colors.danger,
  fontSize: typography.size.xs,
};

const Select = React.forwardRef(function Select(
  {
    id,
    label,
    ariaLabel,
    ariaLabelledby,
    error,
    descriptionId,
    children,
    options,
    loading = false,
    emptyLabel = "No options available",
    style,
    onFocus,
    onBlur,
    ...selectProps
  },
  ref
) {
  const [isFocused, setIsFocused] = useState(false);
  const fallbackId = useId();
  const selectId = id || fallbackId;
  const errorId = error ? `${selectId}-error` : undefined;
  const { disabled: disabledProp, ...restSelectProps } = selectProps;
  const isDisabled = Boolean(disabledProp) || loading;
  const describedByIds = [descriptionId, errorId].filter(Boolean).join(" ") || undefined;
  const computedAriaLabel =
    !label && !ariaLabel && !ariaLabelledby ? restSelectProps.placeholder || undefined : ariaLabel;

  const selectFocusStyle = isFocused
    ? {
        borderColor: error ? colors.danger : colors.accent,
        boxShadow: `0 0 0 3px ${error ? colors.dangerBorder : colors.focusRing}`,
      }
    : null;

  const mergedSelectStyle = {
    ...selectBaseStyle,
    ...(error ? selectErrorStyle : null),
    ...selectFocusStyle,
    ...(isDisabled ? selectDisabledStyle : null),
    ...style,
  };

  const resolvedOptions = Array.isArray(options) ? options : [];
  const shouldRenderOptions = !children;
  const hasOptions = resolvedOptions.length > 0;

  return (
    <>
      {label && (
        <label htmlFor={selectId} style={labelBaseStyle}>
          {label}
        </label>
      )}
      <select
        id={selectId}
        aria-label={computedAriaLabel}
        aria-labelledby={ariaLabelledby}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={describedByIds}
        aria-busy={loading || undefined}
        disabled={isDisabled}
        style={mergedSelectStyle}
        ref={ref}
        onFocus={(event) => {
          setIsFocused(true);
          onFocus && onFocus(event);
        }}
        onBlur={(event) => {
          setIsFocused(false);
          onBlur && onBlur(event);
        }}
        {...restSelectProps}
      >
        {children}
        {shouldRenderOptions &&
          (loading ? (
            <option value="" disabled>
              Loading...
            </option>
          ) : hasOptions ? (
            resolvedOptions.map((opt) => (
              <option key={opt.value ?? opt} value={opt.value ?? opt}>
                {opt.label ?? opt}
              </option>
            ))
          ) : (
            <option value="" disabled>
              {emptyLabel}
            </option>
          ))}
      </select>
      {error && (
        <div id={errorId} style={errorTextStyle}>
          {error}
        </div>
      )}
    </>
  );
});

export default Select;
